import { useState } from "react";
import type {
    AvatarShape,
    AvatarSize,
    AvatarTone,
    AvatarVariant,
} from "@design-system/Avatar/Avatar";
import { HStack } from "@design-system/HStack/HStack";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import {
    avatarContentLabels,
    avatarContents,
    avatarShapes,
    avatarSizes,
    avatarTones,
    avatarVariants,
    pastelColors,
    pastelColorValue,
    stackForShape,
    toAvatarGroupSize,
    type AvatarContent,
    type PastelColorId,
} from "./AvatarPlayground.lib";
import {
    AvatarGroupMatrix,
    PlaygroundAvatarGroup,
} from "./AvatarPlaygroundGroups";
import { AvatarMatrix, AvatarSample } from "./AvatarPlaygroundMatrix";
import { avatarAnatomyVariantId } from "./componentAnatomyAvatarPaints";
import { useAnatomyMode } from "./componentAnatomyMode";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

type ColorOption = PastelColorId | "none";

/** Ready-components playground for Avatar and AvatarGroup, with the x-ray anatomy. */
export const AvatarPlayground = () => {
    const [size, setSize] = useState<AvatarSize>("md");
    const [shape, setShape] = useState<AvatarShape>("circle");
    const [variant, setVariant] = useState<AvatarVariant>("solid");
    const [tone, setTone] = useState<AvatarTone>("colorful");
    const [content, setContent] = useState<AvatarContent>("initial");
    const [colorOption, setColorOption] = useState<ColorOption>("peach");

    const isNeutral = tone === "neutral";
    const pastelColor = pastelColorValue(
        colorOption === "none" ? null : colorOption,
    );
    const color = isNeutral ? undefined : pastelColor;
    const groupSize = toAvatarGroupSize(size);
    const anatomy = useAnatomyMode({
        component: "avatar",
        variantId: avatarAnatomyVariantId({ shape, variant, tone }),
        sizeId: size,
        isDisabled: false,
        avatarContent: { size },
    });

    const summary = `${size} · ${shape} · ${variant} · ${tone} · ${avatarContentLabels[content]}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root<AvatarSize>
                            value={size}
                            onValueChange={(value) => {
                                if (value) {
                                    setSize(value);
                                }
                            }}
                        >
                            {avatarSizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Shape</h2>
                        <ToggleGroup.Root<AvatarShape>
                            value={shape}
                            onValueChange={(value) => {
                                if (value) {
                                    setShape(value);
                                }
                            }}
                        >
                            {avatarShapes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Variant</h2>
                        <ToggleGroup.Root<AvatarVariant>
                            value={variant}
                            onValueChange={(value) => {
                                if (value) {
                                    setVariant(value);
                                }
                            }}
                        >
                            {avatarVariants.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Tone</h2>
                        <ToggleGroup.Root<AvatarTone>
                            value={tone}
                            onValueChange={(value) => {
                                if (value) {
                                    setTone(value);
                                }
                            }}
                        >
                            {avatarTones.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Content</h2>
                        <ToggleGroup.Root<AvatarContent>
                            value={content}
                            onValueChange={(value) => {
                                if (value) {
                                    setContent(value);
                                }
                            }}
                        >
                            {avatarContents
                                .filter((option) => option !== "initialColor")
                                .map((option) => (
                                    <ToggleGroup.Item
                                        key={option}
                                        value={option}
                                    >
                                        {avatarContentLabels[option]}
                                    </ToggleGroup.Item>
                                ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Color</h2>
                        <ToggleGroup.Root<ColorOption>
                            value={isNeutral ? null : colorOption}
                            disabled={isNeutral}
                            onValueChange={(value) => {
                                if (value) {
                                    setColorOption(value);
                                }
                            }}
                        >
                            <ToggleGroup.Item value="none">
                                none
                            </ToggleGroup.Item>
                            {pastelColors.map((option) => (
                                <ToggleGroup.Item
                                    key={option.id}
                                    value={option.id}
                                >
                                    {option.value}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <AvatarMatrix />
                        <AvatarGroupMatrix />
                    </>
                }
                output={JSON.stringify(
                    {
                        size,
                        shape,
                        variant,
                        tone,
                        content,
                        color: color ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <HStack gap={24} align="center">
                        <AvatarSample
                            content={content}
                            size={size}
                            shape={shape}
                            variant={variant}
                            tone={tone}
                            color={color}
                        />
                        {groupSize !== undefined && (
                            <PlaygroundAvatarGroup
                                people={stackForShape(shape)}
                                size={groupSize}
                            />
                        )}
                    </HStack>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
